'use client'

import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from 'motion/react'
import React, { useRef } from 'react'

import { cn } from '@/utilities/ui'

type Props = {
  texto: string
  className?: string
  /** Opacidade da «cópia apagada» (padrão 0.3, como na referência). */
  opacidadeBase?: number
}

/**
 * Frase grande que se «acende» letra a letra conforme o scroll (cópia a 30 % por baixo, cópia nítida
 * que ganha opacidade). Cada palavra é um `span` para não quebrar a meio.
 */
export function LetrasScroll({ texto, className, opacidadeBase = 0.3 }: Props) {
  const ref = useRef<HTMLParagraphElement>(null)
  const reduce = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.85', 'end 0.45'],
  })
  const palavras = texto.split(/\s+/).filter(Boolean)
  const total = palavras.reduce((n, p) => n + p.length, 0)
  // Offset inicial de cada palavra (pré-calculado: nada é reatribuído durante o render).
  const inicios = palavras.reduce<number[]>((acc, p, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + palavras[i - 1].length)
    return acc
  }, [])

  return (
    <p ref={ref} className={cn('ag-heading flex flex-wrap', className)} aria-label={texto}>
      {palavras.map((palavra, wi) => {
        const inicio = inicios[wi]
        return (
          <span key={wi} className="relative mr-[0.28em] whitespace-nowrap" aria-hidden>
            {palavra.split('').map((letra, li) => (
              <Letra
                key={li}
                letra={letra}
                progress={scrollYProgress}
                inicio={(inicio + li) / total}
                fim={(inicio + li + 1) / total}
                base={opacidadeBase}
                estatico={Boolean(reduce)}
              />
            ))}
          </span>
        )
      })}
    </p>
  )
}

function Letra({
  letra,
  progress,
  inicio,
  fim,
  base,
  estatico,
}: {
  letra: string
  progress: MotionValue<number>
  inicio: number
  fim: number
  base: number
  estatico: boolean
}) {
  const opacity = useTransform(progress, [inicio, fim], [0, 1])
  return (
    <span className="relative inline-block">
      <span style={{ opacity: base }}>{letra}</span>
      <motion.span className="absolute inset-0" style={{ opacity: estatico ? 1 : opacity }}>
        {letra}
      </motion.span>
    </span>
  )
}
